'use client';

import { useRef, useState, useCallback } from 'react';
import BlurImage from '@/components/BlurImage';
import Link from 'next/link';
import siteTheme from '@/lib/theme';
import type { HomeSectionCategoryGrid } from '@/lib/cms';
import ArrowIcon from '@/components/ArrowIcon';
import { parseMarkdown } from '@/lib/markdown';
const { colors, fonts } = siteTheme;

const pad = 'clamp(2rem, 6vw, 8rem)';
const section = 'clamp(7rem, 11vw, 13rem)';

export default function HotelOverview({ data }: { data: HomeSectionCategoryGrid }) {
  const CARDS = data.cards.map((c) => ({
    label:     c.label,
    subtitle:  c.subtitle,
    body:      c.body,
    img:       c.image?.url ?? '',
    imgHover:  c.image_hover?.url ?? '',
    href:      c.href,
  }));
  const heading        = data.heading;
  const subtitle       = data.subtitle;
  const learnMoreLabel = data.learn_more_label;
  const learnMoreHref  = data.learn_more_href;
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const index = Math.round(el.scrollLeft / el.offsetWidth);
    setActiveIndex(index);
  }, []);

  const scrollTo = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.offsetWidth, behavior: 'smooth' });
  };

  return (
    <>
      {/* ── DESKTOP: cream bg, label above portrait grid ── */}
      <section className="hidden lg:block" style={{ backgroundColor: '#1c1710', padding: `${section} ${pad}` }}>
        <div style={{ maxWidth: '144rem', margin: '0 auto' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap',
            justifyContent: 'space-between', alignItems: 'flex-end',
            gap: '2rem', marginBottom: 'clamp(3.5rem, 5vw, 5.5rem)',
          }}>
            <div>
              <h2 className="section-title" style={{ fontSize: 'clamp(2.4rem, 3vw, 4rem)', color: colors.gold, letterSpacing: '0.06em', marginBottom: '0.8rem' }}>
                {heading}
              </h2>
              <p className="section-body" style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.8rem)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: '2.4rem' }}>
                {subtitle && <span style={{ fontFamily: fonts.script, fontSize: '2.2em', lineHeight: 0.8, verticalAlign: 'baseline' }}>{subtitle.charAt(0)}</span>}
                {subtitle ? subtitle.slice(1) : null}
              </p>
            </div>
            <Link href={learnMoreHref || '#'} style={{
              fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: colors.gold, display: 'inline-flex', alignItems: 'center', gap: '0.8rem',
              transition: 'gap 0.2s ease', textDecoration: 'none', flexShrink: 0,
            }}>
              {learnMoreLabel} <ArrowIcon />
            </Link>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'clamp(1.6rem, 2.5vw, 3rem)' }}>
            {CARDS.map((card) => (
              <Link key={card.label} href={card.href} className="block group" style={{ textDecoration: 'none' }}>
                {/* Label above card */}
                <p className="card-meta" style={{ marginBottom: '1.6rem' }}>
                  {card.label}
                </p>
                {/* Card image with hover overlay */}
                <div style={{ position: 'relative', paddingBottom: '135%', overflow: 'hidden', backgroundColor: '#111' }}>
                  {/* Normal image */}
                  {card.img && (
                    <BlurImage
                      src={card.img} alt={card.label} fill
                      loading="lazy"
                      className={`object-cover transition-all duration-700 ease-out group-hover:scale-105${card.imgHover ? ' group-hover:opacity-0' : ''}`}
                      sizes="25vw"
                    />
                  )}
                  {/* Hover image (optional) */}
                  {card.imgHover && (
                    <BlurImage
                      src={card.imgHover} alt={card.label} fill
                      className="object-cover transition-opacity duration-700 ease-out opacity-0 group-hover:opacity-100 group-hover:scale-105"
                      sizes="25vw"
                    />
                  )}
                  {/* Dark overlay — appears on hover */}
                  <div className="absolute inset-0 transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                    style={{ background: 'linear-gradient(to top, rgba(5,10,20,0.95) 0%, rgba(5,10,20,0.6) 50%, rgba(5,10,20,0.15) 100%)' }}
                  />
                  {/* Text overlay — slides up on hover */}
                  <div className="absolute inset-0 flex flex-col justify-end transition-all duration-500 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100"
                    style={{ padding: 'clamp(1.8rem, 2.5vw, 2.8rem)' }}>
                    <p className="card-meta" style={{ fontSize: '1.3rem', letterSpacing: '0.18em', marginBottom: '0.8rem' }}>
                      {card.subtitle}
                    </p>
                    <p className="section-body" style={{ fontSize: '1.4rem', lineHeight: 1.65, color: 'rgba(255,255,255,0.75)', marginBottom: '1.8rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }}>
                      {card.body}
                    </p>
                    <span style={{
                      fontFamily: fonts.sans, fontSize: '1.3rem', fontWeight: 300,
                      letterSpacing: '0.06em', color: '#fff',
                      border: '1px solid rgba(255,255,255,0.35)',
                      padding: '0.9rem 1.8rem',
                      display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
                      width: 'fit-content',
                    }}>
                      More information <ArrowIcon variant="short" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── MOBILE: dark bg, header text + swipe carousel ── */}
      <section className="lg:hidden" style={{ backgroundColor: '#1c1710' }}>
        {/* Header */}
        <div style={{ padding: 'clamp(5rem, 8vw, 7rem) clamp(2rem, 5vw, 4rem) 3rem' }}>
          <h2 className="section-title" style={{ fontSize: 'clamp(2.8rem, 7vw, 4rem)', color: '#fff', marginBottom: '1.6rem' }}>
            {heading}
          </h2>
          <p className="section-body" style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.8rem)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: '2.4rem' }}>
            {subtitle && <span style={{ fontFamily: fonts.script, fontSize: '2.2em', lineHeight: 0.8, verticalAlign: 'baseline' }}>{subtitle.charAt(0)}</span>}
            {subtitle ? subtitle.slice(1) : null}
          </p>
          <Link href={learnMoreHref || '#'} style={{
            fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.55)',
            display: 'inline-flex', alignItems: 'center', gap: '0.8rem',
            textDecoration: 'none',
          }}>
            {learnMoreLabel} <ArrowIcon />
          </Link>
        </div>

        {/* Swipe carousel */}
        <div style={{ overflow: 'hidden', width: '100%' }}>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            display: 'flex',
            overflowX: 'scroll',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            width: '100%',
          }}
        >
          {CARDS.map((card, i) => (
            <div
              key={card.label}
              style={{
                flex: '0 0 100%',
                width: '100%',
                minWidth: '100%',
                scrollSnapAlign: 'start',
                padding: '0 clamp(2rem, 5vw, 4rem)',
                boxSizing: 'border-box',
              }}
            >
              {/* Image with overlay label */}
              <div style={{ position: 'relative', paddingBottom: '75%', overflow: 'hidden', backgroundColor: '#111' }}>
                {card.img && (
                  // loading="lazy": mobile images hidden on desktop should not
                  // eagerly download on large viewports — saves bandwidth.
                  <BlurImage
                    src={card.img} alt={card.label} fill
                    loading="lazy"
                    className="object-cover"
                    sizes="(max-width: 768px) 85vw, 25vw"
                  />
                )}
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)' }} />
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <p className="card-meta" style={{ fontSize: '1.6rem', letterSpacing: '0.22em', color: '#fff', textAlign: 'center' }}>
                    {card.label}
                  </p>
                </div>
              </div>

              {/* Below image: subtitle + body + book + more info */}
              <div style={{ padding: '2.4rem 0 3.5rem' }}>
                <p className="card-meta" style={{ color: 'rgba(255,255,255,0.45)', marginBottom: '1.2rem' }}>
                  {card.subtitle}
                </p>
                <div className="section-body cms-rich-text" style={{ fontSize: '1.5rem', lineHeight: 1.7, color: 'rgba(255,255,255,0.55)' }} dangerouslySetInnerHTML={{ __html: parseMarkdown(card.body) }} />

              </div>
            </div>
          ))}
        </div>
        </div>

        {/* Dots indicator — active dot is wider */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.6rem', paddingBottom: '4rem' }}>
          {CARDS.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollTo(i)}
              style={{
                width: i === activeIndex ? '2rem' : '0.6rem',
                height: '0.3rem',
                backgroundColor: i === activeIndex ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)',
                borderRadius: '2px',
                transition: 'all 0.3s ease',
                border: 'none', cursor: 'pointer', padding: 0,
              }}
            />
          ))}
        </div>
      </section>
    </>
  );
}
